import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import { 
  Users,
  DollarSign,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  TrendingUp,
  UserPlus,
  Wallet,
  ArrowUpRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

const AdminAffiliates = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, withdrawalsRes] = await Promise.all([
        axios.get(`${API}/admin/affiliate/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/affiliate/withdrawals`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setWithdrawals(withdrawalsRes.data.withdrawals || []);
    } catch (e) {
      toast.error('Error al cargar datos de afiliados');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateWithdrawal = async (withdrawalId, status) => {
    if (status === 'completed' && !window.confirm('Confirma que ya enviaste el pago al destino indicado. Marcar el retiro como completado no envía dinero automáticamente.')) return;
    setProcessing(withdrawalId);
    try {
      await axios.put(
        `${API}/admin/affiliate/withdrawals/${withdrawalId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Retiro ${status === 'completed' ? 'aprobado' : 'rechazado'}`);
      fetchData();
    } catch (e) {
      toast.error('Error al actualizar retiro');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
      processing: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    };
    
    const icons = {
      completed: <CheckCircle2 size={12} className="mr-1" />,
      pending: <Clock size={12} className="mr-1" />,
      rejected: <XCircle size={12} className="mr-1" />,
      processing: <Loader2 size={12} className="mr-1 animate-spin" />
    };
    
    return (
      <Badge className={styles[status] || styles.pending}>
        {icons[status]}
        {status?.toUpperCase()}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-DO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const pendingWithdrawals = withdrawals.filter(w => w.status === 'pending');
  const paymentDestination = (w) => {
    const details = w.payment_details;
    if (typeof details === 'string') return details || 'Destino no registrado';
    return details?.destination || Object.values(details || {}).join(' · ') || 'Destino no registrado';
  };

  if (loading) {
    return (
      <AdminLayout title="Sistema de Afiliados" subtitle="Cargando...">
        <div className="flex items-center justify-center h-64">
          <Loader2 size={40} className="text-amber-400 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Sistema de Afiliados" subtitle="Gestión del programa de referidos multinivel">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Users size={20} className="text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.total_affiliates || 0}</p>
                <p className="text-gray-500 text-xs">Afiliados Activos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <UserPlus size={20} className="text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.total_referrals || 0}</p>
                <p className="text-gray-500 text-xs">Total Referidos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <DollarSign size={20} className="text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">${(stats?.total_commissions_paid || 0).toFixed(2)}</p>
                <p className="text-gray-500 text-xs">Comisiones Totales</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Wallet size={20} className="text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">${(stats?.pending_withdrawals?.amount || 0).toFixed(2)}</p>
                <p className="text-gray-500 text-xs">Retiros Pendientes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="pending" className="space-y-6">
        <TabsList className="bg-gray-900 border border-gray-800">
          <TabsTrigger value="pending" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
            Pendientes ({pendingWithdrawals.length})
          </TabsTrigger>
          <TabsTrigger value="all" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
            Todos los Retiros
          </TabsTrigger>
          <TabsTrigger value="stats" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
            Estadísticas
          </TabsTrigger>
        </TabsList>

        {/* Pending Withdrawals Tab */}
        <TabsContent value="pending">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock size={20} className="text-yellow-400" />
                Retiros Pendientes de Aprobación
              </CardTitle>
              <CardDescription className="text-gray-500">
                Revisa y procesa las solicitudes de retiro de afiliados
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pendingWithdrawals.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle2 size={48} className="text-green-400 mx-auto mb-4" />
                  <p className="text-gray-400">No hay retiros pendientes</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-800">
                      <TableHead className="text-gray-400">Afiliado</TableHead>
                      <TableHead className="text-gray-400">Monto</TableHead>
                      <TableHead className="text-gray-400">Método</TableHead>
                      <TableHead className="text-gray-400">Fecha</TableHead>
                      <TableHead className="text-gray-400 text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingWithdrawals.map((w) => (
                      <TableRow key={w.id} className="border-gray-800">
                        <TableCell>
                          <div>
                            <p className="text-white font-medium">{w.user_name || 'Usuario'}</p>
                            <p className="text-gray-500 text-xs">{w.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-white font-bold text-lg">${w.amount.toFixed(2)}</span>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-gray-700 text-gray-300">
                            {w.payment_method?.toUpperCase()}
                          </Badge>
                          <p className="text-gray-300 text-xs mt-1 break-all max-w-52">{paymentDestination(w)}</p>
                        </TableCell>
                        <TableCell className="text-gray-400">
                          {formatDate(w.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleUpdateWithdrawal(w.id, 'completed')}
                              disabled={processing === w.id}
                              className="bg-green-600 hover:bg-green-500"
                            >
                              {processing === w.id ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <CheckCircle2 size={14} />
                              )}
                              <span className="ml-1">Aprobar</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUpdateWithdrawal(w.id, 'rejected')}
                              disabled={processing === w.id}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            >
                              <XCircle size={14} />
                              <span className="ml-1">Rechazar</span>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Withdrawals Tab */}
        <TabsContent value="all">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Historial de Retiros</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800">
                    <TableHead className="text-gray-400">Afiliado</TableHead>
                    <TableHead className="text-gray-400">Monto</TableHead>
                    <TableHead className="text-gray-400">Método</TableHead>
                    <TableHead className="text-gray-400">Estado</TableHead>
                    <TableHead className="text-gray-400">Fecha</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {withdrawals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                        No hay retiros registrados
                      </TableCell>
                    </TableRow>
                  ) : (
                    withdrawals.map((w) => (
                      <TableRow key={w.id} className="border-gray-800">
                        <TableCell>
                          <div>
                            <p className="text-white font-medium">{w.user_name || 'Usuario'}</p>
                            <p className="text-gray-500 text-xs">{w.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-white font-bold">${w.amount.toFixed(2)}</span>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-gray-700 text-gray-300">
                            {w.payment_method?.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(w.status)}</TableCell>
                        <TableCell className="text-gray-400">
                          {formatDate(w.created_at)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stats Tab */}
        <TabsContent value="stats">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp size={20} className="text-green-400" />
                  Resumen de Comisiones
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-4 rounded-lg bg-gray-800/50">
                  <span className="text-gray-400">Total Pagado</span>
                  <span className="text-2xl font-bold text-green-400">
                    ${(stats?.completed_withdrawals?.amount || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center p-4 rounded-lg bg-gray-800/50">
                  <span className="text-gray-400">Retiros Completados</span>
                  <span className="text-xl font-bold text-white">
                    {stats?.completed_withdrawals?.count || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center p-4 rounded-lg bg-gray-800/50">
                  <span className="text-gray-400">Pendiente de Pago</span>
                  <span className="text-xl font-bold text-yellow-400">
                    ${(stats?.pending_withdrawals?.amount || 0).toFixed(2)}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Users size={20} className="text-blue-400" />
                  Red de Afiliados
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-4 rounded-lg bg-gray-800/50">
                  <span className="text-gray-400">Afiliados con Ganancias</span>
                  <span className="text-xl font-bold text-white">
                    {stats?.total_affiliates || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center p-4 rounded-lg bg-gray-800/50">
                  <span className="text-gray-400">Usuarios Referidos</span>
                  <span className="text-xl font-bold text-white">
                    {stats?.total_referrals || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center p-4 rounded-lg bg-gray-800/50">
                  <span className="text-gray-400">Tasa de Conversión</span>
                  <span className="text-xl font-bold text-purple-400">
                    {stats?.total_referrals > 0 
                      ? ((stats?.total_affiliates / stats?.total_referrals) * 100).toFixed(1) 
                      : 0}%
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default AdminAffiliates;
